import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ConsentService } from '@core/services/consent/consent.service';
import { ConsentPreferences } from '@core/services/consent/consent.types';
import { LucideDynamicIcon } from '@lucide/angular';
import { BarChart3, Cookie, Shield, Target, X } from '@core/icons/lucide.icons';

/**
 * Cookie Consent Banner
 *
 * GDPR-compliant cookie consent banner with:
 * - Accept All / Reject All quick actions
 * - Customize preferences modal
 * - Persistent dismissal on choice
 * - Accessible keyboard navigation
 */
@Component({
  selector: 'app-cookie-consent',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, LucideDynamicIcon],
  template: `
    @if (consentService.browser.isBrowser && consentService.showBanner()) {
      <!-- Backdrop only shown when customizing preferences (modal mode) -->
      @if (showCustomize()) {
        <div class="animate-in fade-in fixed inset-0 z-9998 bg-black/30 duration-300"></div>
      }

      <!-- Banner -->
      <div
        class="animate-in slide-in-from-bottom fixed right-0 bottom-0 left-0 z-9999 border-t border-gray-200 bg-white shadow-2xl duration-500"
        role="dialog"
        aria-label="Cookie consent"
        [attr.aria-describedby]="showCustomize() ? null : 'cookie-consent-description'"
      >
        <div class="mx-auto max-w-7xl px-4 py-2 sm:px-6 sm:py-3 lg:px-8">
          @if (!showCustomize()) {
            <!-- Main Banner View -->
            <div class="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-4">
              <!-- Text -->
              <p id="cookie-consent-description" class="flex-1 text-xs text-gray-600 sm:text-sm">
                We use cookies to improve your experience and analyze site traffic.
                <a routerLink="/cookies" class="ml-1 text-blue-600 underline hover:text-blue-800">
                  Read our cookie policy
                </a>
              </p>

              <!-- Buttons -->
              <div class="flex flex-row gap-2 lg:shrink-0">
                <button
                  (click)="onCustomize()"
                  class="flex-1 rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 sm:flex-none sm:px-4 sm:text-sm"
                >
                  Customize
                </button>
                <button
                  (click)="onRejectAll()"
                  class="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:flex-none sm:px-4 sm:text-sm"
                >
                  Reject All
                </button>
                <button
                  (click)="onAcceptAll()"
                  class="flex-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-blue-700 sm:flex-none sm:px-4 sm:text-sm"
                >
                  Accept All
                </button>
              </div>
            </div>
          } @else {
            <!-- Customize View -->
            <div class="mx-auto max-w-3xl">
              <div class="mb-6 flex items-center justify-between">
                <h2 class="text-xl font-semibold text-gray-900">Cookie Preferences</h2>
                <button
                  (click)="showCustomize.set(false)"
                  class="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Close preferences"
                >
                  <svg [lucideIcon]="XIcon" class="h-5 w-5" />
                </button>
              </div>

              <div class="space-y-4">
                <!-- Necessary Cookies -->
                <div class="flex items-start gap-4 rounded-lg bg-gray-50 p-4">
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-100"
                  >
                    <svg [lucideIcon]="ShieldIcon" class="h-5 w-5 text-green-600" />
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center justify-between">
                      <h3 class="font-medium text-gray-900">Strictly Necessary</h3>
                      <span
                        class="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700"
                      >
                        Always Active
                      </span>
                    </div>
                    <p class="mt-1 text-sm text-gray-600">
                      Essential for the website to function. Cannot be disabled.
                    </p>
                  </div>
                </div>

                <!-- Analytics Cookies -->
                <div class="flex items-start gap-4 rounded-lg border border-gray-200 bg-white p-4">
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100"
                  >
                    <svg [lucideIcon]="BarChart3Icon" class="h-5 w-5 text-blue-600" />
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center justify-between">
                      <h3 class="font-medium text-gray-900">Analytics</h3>
                      <label class="relative inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          [checked]="customPreferences().analytics"
                          (change)="togglePreference('analytics')"
                          class="peer sr-only"
                        />
                        <div
                          class="peer h-6 w-11 rounded-full bg-gray-200 peer-checked:bg-blue-600 peer-focus:ring-4 peer-focus:ring-blue-300 peer-focus:outline-none after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white"
                        ></div>
                      </label>
                    </div>
                    <p class="mt-1 text-sm text-gray-600">
                      Help us understand how visitors interact with our website. This data is
                      anonymized.
                    </p>
                  </div>
                </div>

                <!-- Functional Cookies -->
                <div class="flex items-start gap-4 rounded-lg border border-gray-200 bg-white p-4">
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-100"
                  >
                    <svg [lucideIcon]="CookieIcon" class="h-5 w-5 text-purple-600" />
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center justify-between">
                      <h3 class="font-medium text-gray-900">Functional</h3>
                      <label class="relative inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          [checked]="customPreferences().functional"
                          (change)="togglePreference('functional')"
                          class="peer sr-only"
                        />
                        <div
                          class="peer h-6 w-11 rounded-full bg-gray-200 peer-checked:bg-blue-600 peer-focus:ring-4 peer-focus:ring-blue-300 peer-focus:outline-none after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white"
                        ></div>
                      </label>
                    </div>
                    <p class="mt-1 text-sm text-gray-600">
                      Remember your preferences like language and search filters.
                    </p>
                  </div>
                </div>

                <!-- Marketing Cookies -->
                <div class="flex items-start gap-4 rounded-lg border border-gray-200 bg-white p-4">
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-100"
                  >
                    <svg [lucideIcon]="TargetIcon" class="h-5 w-5 text-orange-600" />
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center justify-between">
                      <h3 class="font-medium text-gray-900">Marketing</h3>
                      <label class="relative inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          [checked]="customPreferences().marketing"
                          (change)="togglePreference('marketing')"
                          class="peer sr-only"
                        />
                        <div
                          class="peer h-6 w-11 rounded-full bg-gray-200 peer-checked:bg-blue-600 peer-focus:ring-4 peer-focus:ring-blue-300 peer-focus:outline-none after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white"
                        ></div>
                      </label>
                    </div>
                    <p class="mt-1 text-sm text-gray-600">
                      Used for targeted advertising and measuring ad effectiveness.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-6">
                <button
                  (click)="showCustomize.set(false)"
                  class="rounded-lg bg-gray-100 px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  (click)="onSavePreferences()"
                  class="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    }
  `,
  styles: `
    .animate-in {
      animation-duration: 300ms;
      animation-timing-function: ease-out;
      animation-fill-mode: both;
    }
    .fade-in {
      animation-name: fadeIn;
    }
    .slide-in-from-bottom {
      animation-name: slideInFromBottom;
    }
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
    @keyframes slideInFromBottom {
      from {
        transform: translateY(100%);
      }
      to {
        transform: translateY(0);
      }
    }
  `,
})
export class CookieConsentComponent {
  readonly consentService = inject(ConsentService);

  // Icons
  readonly XIcon = X;
  readonly CookieIcon = Cookie;
  readonly ShieldIcon = Shield;
  readonly BarChart3Icon = BarChart3;
  readonly TargetIcon = Target;

  // State
  showCustomize = signal(false);
  customPreferences = signal<ConsentPreferences>({
    necessary: true,
    analytics: false,
    functional: false,
    marketing: false,
  });

  onAcceptAll(): void {
    this.consentService.acceptAll();
  }

  onRejectAll(): void {
    this.consentService.rejectAll();
  }

  onCustomize(): void {
    // Initialize with current preferences or defaults
    const current = this.consentService.state().preferences;
    this.customPreferences.set({ ...current });
    this.showCustomize.set(true);
  }

  togglePreference(key: 'analytics' | 'functional' | 'marketing'): void {
    this.customPreferences.update((prefs) => ({
      ...prefs,
      [key]: !prefs[key],
    }));
  }

  onSavePreferences(): void {
    this.consentService.customizeConsent(this.customPreferences());
    this.showCustomize.set(false);
  }
}
